import { createReducer } from 'src/helpers/reducer';
import { isEmpty } from '@jjj/components';
import { t } from 'src/i18n';
import { IChangePropertyPayload } from '@dash/editForm';
import { ISendRequestParams } from '@dash/actions';
import actionsTypes from './actionTypes';
import {
  Attribute,
  IAddSkillModalState,
  IChangeModalVisibilityPayload,
  IFieldTypes,
  ILoadFormInfoSuccessPayload,
} from './types';

function validate(attr: Attribute, value: IFieldTypes) {
  let errorMessage = '';

  const requiredAttributes: Attribute[] = ['skill', 'tier'];

  if (requiredAttributes.includes(attr)) {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t(`dash.clients.components.addNewSkillModal.${attr}_label`),
      });
    }
  }

  return errorMessage;
}

const getInitialField = (fieldName: Attribute, value: string | null = null) => ({
  name: fieldName,
  value,
  touched: false,
  error: validate(fieldName, value || ''),
});

const initialEditForm = {
  skill: getInitialField('skill'),
  tier: getInitialField('tier'),
};

const initState: IAddSkillModalState = {
  isLoading: false,
  isModalVisible: false,
  editForm: initialEditForm,
  isFormInfoLoading: false,
  formInfo: undefined,
  loadFormInfoAction: undefined,
  isAddNewSkillLoading: false,
};

function onChangeModalVisibility(state: IAddSkillModalState, { payload }: { payload: IChangeModalVisibilityPayload }) {
  const { loadFormInfoAction } = payload;

  return {
    ...state,
    isModalVisible: !state.isModalVisible,
    loadFormInfoAction,
  };
}

function onChangeProperty(
  state: IAddSkillModalState,
  { payload }: { payload: IChangePropertyPayload<Attribute, IFieldTypes> },
): IAddSkillModalState {
  const { attr, value } = payload;
  const { editForm } = state;

  const getValue = (val: IFieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, val),
    name: attribute,
    touched: true,
  });

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onLoadFormInfoSuccess(state: IAddSkillModalState, { payload }: { payload: ILoadFormInfoSuccessPayload }) {
  return {
    ...state,
    formInfo: payload.formInfo,
    isFormInfoLoading: false,
  };
}

function onProcessActionRequest(state: IAddSkillModalState, { payload }: { payload: ISendRequestParams }) {
  return {
    ...state,
    isFormInfoLoading: payload.key === 'add_skill_form_data' ? true : state.isFormInfoLoading,
  };
}

function onAddNewSkillRequest(state: IAddSkillModalState) {
  return {
    ...state,
    isAddNewSkillLoading: true,
  };
}

function onAddNewSkillSuccess(state: IAddSkillModalState) {
  return {
    ...state,
    isAddNewSkillLoading: false,
  };
}

function onAddNewSkillError(state: IAddSkillModalState) {
  return {
    ...state,
    isAddNewSkillLoading: false,
  };
}

function onClearState() {
  return {
    ...initState,
  };
}

export default createReducer(initState, {
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.PROCESS_ACTION_REQUEST]: onProcessActionRequest,
  [actionsTypes.CHANGE_MODAL_VISIBILITY]: onChangeModalVisibility,
  [actionsTypes.LOAD_FORM_INFO_SUCCESS]: onLoadFormInfoSuccess,
  [actionsTypes.ADD_NEW_SKILL_REQUEST]: onAddNewSkillRequest,
  [actionsTypes.ADD_NEW_SKILL_SUCCESS]: onAddNewSkillSuccess,
  [actionsTypes.ADD_NEW_SKILL_ERROR]: onAddNewSkillError,
  [actionsTypes.CLEAR_STATE]: onClearState,
});
